import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useChat } from "../hooks/useChat";
import { Avatar } from "./Avatar";
import { AddMemberDialog } from "./AddMemberDialog";
import { X } from "lucide-react";
import styles from "./ChannelInfo.module.css";

interface Member {
  userId: string;
  displayName: string;
  email: string;
  role: string;
  joinedAt: string;
}

interface ChannelInfoProps {
  channelId: string;
  currentUserId: string;
  onClose: () => void;
  onLeaveChannel?: () => void;
}

export function ChannelInfo({ channelId, currentUserId, onClose, onLeaveChannel }: ChannelInfoProps) {
  const { channels, onlineUsers } = useChat();
  const channel = channels.find((c) => c.id === channelId);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);

  const loadMembers = useCallback(async () => {
    try {
      const data = await api.get<{ members: Member[]; unreadCount: number }>(
        `/channels/${channelId}`,
      );
      setMembers(data.members ?? []);
    } catch {
      // Fallback
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleAddMember = async (user: { id: string }) => {
    try {
      const res = await api.post<{ ownerOverride?: boolean }>(
        `/channels/${channelId}/members`,
        { userId: user.id },
      );
      await loadMembers();
      setShowAddMember(false);
      if (res.ownerOverride) {
        alert("Als Owner hinzugefügt — normalerweise können nur Channel-Admins Mitglieder einladen.");
      }
    } catch (err) {
      alert((err as { message?: string }).message ?? "Fehler beim Hinzufügen");
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!confirm("Mitglied entfernen?")) return;
    try {
      await api.delete(`/channels/${channelId}/members/${userId}`);
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
    } catch {
      alert("Fehler beim Entfernen");
    }
  };

  return (
    <div className={styles.panel}>
      {showAddMember && (
        <AddMemberDialog
          title="Mitglied hinzufügen"
          description="Person zum Channel einladen"
          onClose={() => setShowAddMember(false)}
          onSelect={handleAddMember}
        />
      )}

      <div className={styles.header}>
        <h3 className={styles.title}>Channel-Info</h3>
        <button className={styles.closeBtn} onClick={onClose}><X size={18} /></button>
      </div>

      <div className={styles.content}>
        {/* Channel Name */}
        <div className={styles.channelHeader}>
          {(() => {
            const dmPartner = channel?.type === "dm"
              ? members.find((m) => m.userId !== currentUserId)
              : null;
            const displayName = (channel?.type === "dm" && dmPartner?.displayName)
              ? dmPartner.displayName
              : channel?.name ?? "Chat";
            return (
              <>
                <Avatar name={displayName} size="lg" />
                <h4 className={styles.channelName}>{displayName}</h4>
                <span className={styles.channelType}>
                  {channel?.type === "dm" ? "Direktnachricht" : "Gruppe"}
                </span>
              </>
            );
          })()}
        </div>

        {channel?.description && (
          <div className={styles.section}>
            <h5 className={styles.sectionTitle}>Beschreibung</h5>
            <p className={styles.description}>{channel.description}</p>
          </div>
        )}

        {/* Members */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h5 className={styles.sectionTitle}>
              Mitglieder ({members.length})
            </h5>
            {channel?.type === "group" && (
              <button
                className={styles.addBtn}
                onClick={() => setShowAddMember(true)}
              >
                + Einladen
              </button>
            )}
          </div>

          {loading ? (
            <div className={styles.loading}>Laden...</div>
          ) : (
            <div className={styles.memberList}>
              {members.map((member) => (
                <div key={member.userId} className={styles.memberItem}>
                  <Avatar name={member.displayName} size="sm" online={onlineUsers.has(member.userId)} />
                  <div className={styles.memberInfo}>
                    <span className={styles.memberName}>
                      {member.displayName}
                      {member.userId === currentUserId && (
                        <span className={styles.youBadge}>Du</span>
                      )}
                    </span>
                    <span className={styles.memberRole}>{member.role}</span>
                  </div>
                  {channel?.type === "group" &&
                    member.userId !== currentUserId &&
                    member.role !== "admin" && (
                      <button
                        className={styles.removeBtn}
                        onClick={() => handleRemoveMember(member.userId)}
                        aria-label="Entfernen"
                      >
                        <X size={18} />
                      </button>
                    )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Leave / Delete Channel */}
        <div className={styles.section}>
          <button
            className={styles.dangerBtn}
            onClick={async () => {
              const action = channel?.type === "dm" ? "Chat löschen" : "Channel löschen";
              if (!confirm(`${action}? Alle Nachrichten werden gelöscht.`)) return;
              try {
                await api.delete(`/channels/${channelId}`);
                onLeaveChannel?.();
              } catch {
                alert("Fehler beim Löschen");
              }
            }}
          >
            {channel?.type === "dm" ? "🗑️ Chat löschen" : "🗑️ Channel löschen"}
          </button>
        </div>
      </div>
    </div>
  );
}
